import React from "react";
import SbEditable from "storyblok-react";
import Link from "@/components/Link";
import Components from "../components";
import { InstagramLogoIcon, TwitterLogoIcon } from "@phosphor-icons/react";
import Section from "@/components/Section/section";
import styles from "./coach.module.scss";
import Flag from "@/components/Flags/flag";
import TextBlock from "@/components/TextBlock/text-block";
import Divider from "@/components/Divider/divider";
import Button from "@/components/Button/button";
import LayoutGrid from "@/components/Layout/layout-grid";
import CoachCard from "@/components/Coaches/coach-card";
import classnames from "classnames";
import Main from "@/components/Layout/main";
import FormNewsletter from "@/components/FormNewsletter/form-newsletter";
import { CoachStoryblok } from "@/types/component-types-sb";
import { GetCoaches } from "@/lib/storyblok-gapi";

interface Props {
  content: CoachStoryblok;
  coaches: GetCoaches;
  id: string;
}

const CoachDetail = (props: Props) => {
  const {
    id,
    content: {
      image,
      intro,
      languages,
      location,
      name,
      nationality,
      body,
      coach_type,
      social_in,
      social_tw,
    },
    coaches,
  } = props;
  const imageBloks = Array.isArray(image) ? image : [];
  const bodyBloks = Array.isArray(body) ? body : [];

  const filteredCoaches = coaches
    .filter((coach) => coach.id !== id)
    .slice(0, 3);

  return (
    // @ts-expect-error
    <SbEditable content={props.content}>
      <Main className="coaches-page">
        <div className={styles.image}>
          {imageBloks.length
            ? imageBloks.map((blok) =>
                React.createElement(Components(blok.component), {
                  key: blok._uid,
                  blok: blok,
                })
              )
            : null}
        </div>
        <Section className={styles.introSection}>
          <aside className={styles.aside}>
            <div className={styles.flag}>
              <Flag country={nationality} className={styles.flagSvg} />
            </div>
            <div className={styles.title}>
              <h1 className="ef-h4 u-mb-0">{name}</h1>
              <p className={`ef-h6 ${styles.type}`}>{coach_type}</p>
            </div>

            {location?.length ? (
              <>
                <p className={styles.detailTitle}>Location</p>
                <ul className={styles.detailList}>
                  {location.map((blok, i) => (
                    <li key={i} className={styles.detailItem}>
                      {blok.content}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {languages?.length ? (
              <>
                <p className={styles.detailTitle}>Languages</p>
                <ul className={styles.detailList}>
                  {languages.map((blok, i) => (
                    <li key={i} className={styles.detailItem}>
                      {blok.content}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            <div className={styles.row}>
              {social_in?.url !== "" && (
                <Link href={social_in?.url}>
                  <InstagramLogoIcon
                    size={16}
                    weight="fill"
                    aria-hidden="true"
                  />
                </Link>
              )}
              {social_tw?.url && (
                <Link href={social_tw.url}>
                  <TwitterLogoIcon size={16} weight="fill" aria-hidden="true" />
                </Link>
              )}
            </div>
          </aside>
          <TextBlock
            content={intro}
            className={styles.intro}
            _uid=""
            component="text-block"
          />
          <Divider className={styles.divider} />
          {bodyBloks.length
            ? bodyBloks.map((blok) =>
                React.createElement(Components(blok.component), {
                  key: blok._uid,
                  blok: blok,
                })
              )
            : null}
        </Section>
        <Section className={classnames(styles.moreCoaches)}>
          <div className={styles.moreTitle}>
            <h2 className="ef-h3 u-mb-0">More Coaches</h2>
            <Button
              link={"/coaches"}
              icon={"IconArrowRight"}
              className="u-hide-s-only"
              style={{ ["--btn-accent" as string]: "var(--color-light-grey)" }}
            >
              See all our coaches
            </Button>
          </div>
          <LayoutGrid
            className={styles.moreCoachesGrid}
            style={{ ["--cols-m" as string]: 1, ["--cols-d" as string]: 3 }}
          >
            {filteredCoaches.length
              ? filteredCoaches.map((blok) => (
                  <CoachCard key={blok._uid} {...blok} />
                ))
              : null}
          </LayoutGrid>
          <FormNewsletter />
        </Section>
      </Main>
    </SbEditable>
  );
};

export default CoachDetail;
